import { jsPDF } from 'jspdf';
import { LampCondition, OrderPriority, OrderStatus } from '../../types/order';
import { getPdfBackgroundDataUrl } from './pdfBackground';

export type OrderReceiptPdfData = {
  protocolNumber?: string | null;
  reporterName?: string;
  address: string;
  neighborhood?: string;
  houseNumberInFront?: string;
  referencePoint?: string;
  notes?: string;
  lampCondition?: LampCondition;
  priority?: OrderPriority;
  status?: OrderStatus;
};

const lampConditionLabel: Record<LampCondition, string> = {
  APAGADA: 'Lampada apagada',
  SEMPRE_ACESA: 'Sempre acesa',
  INTERMITENTE: 'Intermitente',
  INSTALACAO_KIT: 'Instalação de Kit',
};

const priorityLabel: Record<OrderPriority, string> = {
  ALTA: 'Alta',
  MEDIA: 'Media',
  BAIXA: 'Baixa',
};

const statusLabel: Record<OrderStatus, string> = {
  ABERTA: 'Aberta',
  AVULSA: 'Avulsa',
  EM_ANALISE: 'Em Análise',
  EM_ANDAMENTO: 'Em andamento',
  PENDENTE: 'Pendente',
  CONCLUIDA: 'Concluida',
  EM_ATRASO: 'Em atraso',
};

const formatDate = (date: Date) => date.toLocaleDateString('pt-BR');
const formatTime = (date: Date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

export const buildOrderReceiptPdf = async (data: OrderReceiptPdfData, issuedAt: Date) => {
  const document = new jsPDF({ unit: 'mm', format: 'a4' });
  const contentTopOffset = 25;
  const pageWidth = document.internal.pageSize.getWidth();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const pageHeight = document.internal.pageSize.getHeight();
  const backgroundDataUrl = await getPdfBackgroundDataUrl();

  if (backgroundDataUrl) {
    document.addImage(backgroundDataUrl, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  }

  const drawCard = (
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    value: string,
    maxLines = 2,
  ) => {
    document.setDrawColor(222, 226, 230);
    document.setFillColor(248, 249, 250);
    document.roundedRect(x, y, width, height, 2, 2, 'FD');

      document.setTextColor(73, 80, 87);
    document.setFont('helvetica', 'bold');
    document.setFontSize(7.5);
    document.text(label, x + 2.5, y + 3.5);

    document.setTextColor(33, 37, 41);
    document.setFont('helvetica', 'normal');
    document.setFontSize(8);

    const textWidth = width - 6;
    const allLines = document.splitTextToSize(value || '-', textWidth);
    const lines = allLines.slice(0, maxLines);
    if (allLines.length > maxLines && lines.length > 0) {
      lines[lines.length - 1] = `${lines[lines.length - 1]}...`;
    }
    document.text(lines, x + 2.5, y + 8);
  };

  document.setFillColor(52, 152, 219);
  document.roundedRect(margin, 10 + contentTopOffset, contentWidth, 12, 3, 3, 'F');
  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(11);
  document.text('Comprovante de Solicitacao', margin + 5, 16 + contentTopOffset);
  document.setFontSize(7.5);
  document.text('Iluminacao Publica - Atendimento ao Solicitante', margin + 5, 19 + contentTopOffset);

  document.setTextColor(33, 37, 41);

  const colGap = 4;
  const colWidth = (contentWidth - colGap) / 2;

  drawCard(
    margin,
    26 + contentTopOffset,
    colWidth,
    11,
    'PROTOCOLO',
    data.protocolNumber?.trim() || 'Sera definido apos o salvamento',
    1,
  );
  drawCard(
    margin + colWidth + colGap,
    26 + contentTopOffset,
    colWidth,
    11,
    'SOLICITANTE',
    data.reporterName?.trim() || 'Nao informado',
    1,
  );

  drawCard(margin, 40 + contentTopOffset, contentWidth, 13, 'ENDERECO', data.address.trim() || '-', 2);

  drawCard(margin, 56 + contentTopOffset, colWidth, 11, 'BAIRRO', data.neighborhood?.trim() || '-', 1);
  drawCard(
    margin + colWidth + colGap,
    56 + contentTopOffset,
    colWidth,
    11,
    'CASA EM FRENTE',
    data.houseNumberInFront?.trim() || '-',
    1,
  );

  drawCard(margin, 70 + contentTopOffset, colWidth, 11, 'REFERENCIA', data.referencePoint?.trim() || '-', 1);
  drawCard(
    margin + colWidth + colGap,
    70 + contentTopOffset,
    colWidth,
    11,
    'LAMPADA',
    data.lampCondition ? lampConditionLabel[data.lampCondition] : '-',
    1,
  );

  drawCard(margin, 84 + contentTopOffset, colWidth, 11, 'PRIORIDADE', data.priority ? priorityLabel[data.priority] : '-', 1);
  drawCard(
    margin + colWidth + colGap,
    84 + contentTopOffset,
    colWidth,
    11,
    'STATUS INICIAL',
    data.status ? statusLabel[data.status] : '-',
    1,
  );

  drawCard(margin, 98 + contentTopOffset, contentWidth, 18, 'OBSERVACOES', data.notes?.trim() || '-', 3);

  document.setDrawColor(206, 212, 218);
  document.line(margin, 120 + contentTopOffset, pageWidth - margin, 120 + contentTopOffset);
  document.setFont('helvetica', 'normal');
  document.setFontSize(7.5);
  document.text(
    `Emitido em ${formatDate(issuedAt)} as ${formatTime(issuedAt)}. Guarde este comprovante para acompanhamento.`,
    margin,
    125 + contentTopOffset,
  );

  return document;
};